import React from "react";

const FINANCIAL_COLORS = {
  positive: "#2AA97A",
  negative: "#D95656",
};

const BLOCK_NOTE_TEXT_COLORS = {
  green: FINANCIAL_COLORS.positive,
  red: FINANCIAL_COLORS.negative,
  gray: "#636369",
  grey: "#636369",
};

const resolveTextColor = (color) =>
  BLOCK_NOTE_TEXT_COLORS[String(color).toLowerCase()] ?? color;

const isFinancialColor = (color) => {
  const normalized = String(resolveTextColor(color)).toLowerCase();
  return Object.values(FINANCIAL_COLORS).some(
    (financialColor) => financialColor.toLowerCase() === normalized,
  );
};

const getFinancialTone = (text) => {
  const value = String(text ?? "");
  if (/(^|\()\s*\+/.test(value)) return "positive";
  if (/(^|\()\s*-/.test(value)) return "negative";
  return null;
};

const FINANCIAL_VALUE_PATTERN =
  /([+-]?\d[\d.,]*\s*(?:triệu|tỷ|nghìn|đồng)?\s*\(\s*[+-]\s*\d[\d.,]*%\s*\)|[+-]\s*\d[\d.,]*(?:\s*(?:triệu|tỷ|nghìn|đồng)|%))/gi;

const DRAWDOWN_LABEL_PATTERN = /khoảng cách.*đỉnh|drawdown/i;

const renderFinancialText = (text) => {
  const value = String(text ?? "");
  const parts = value.split(FINANCIAL_VALUE_PATTERN);
  if (parts.length === 1) return value;

  return parts.map((part, index) => {
    const tone = getFinancialTone(part);
    return tone ? (
      <span
        key={index}
        style={{ color: FINANCIAL_COLORS[tone], fontWeight: 500 }}
      >
        {part}
      </span>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    );
  });
};

const getAdjacentFinancialTone = (content, index) => {
  const current = String(content[index]?.text ?? "");
  const previous = String(content[index - 1]?.text ?? "");
  const next = String(content[index + 1]?.text ?? "");
  const amountAtEnd = /\d[\d.,]*\s*(?:triệu|tỷ|nghìn|đồng)\s*$/i;
  const signedPercent = /^\s*\(\s*([+-])\s*\d[\d.,]*%\s*\)/;

  const nextSign = next.match(signedPercent)?.[1];
  if (amountAtEnd.test(current) && nextSign) {
    return nextSign === "+" ? "positive" : "negative";
  }

  const currentSign = current.match(signedPercent)?.[1];
  if (amountAtEnd.test(previous) && currentSign) {
    return currentSign === "+" ? "positive" : "negative";
  }

  return null;
};

const getInlineText = (content) => {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .map((item) =>
      item?.type === "link" ? getInlineText(item.content) : (item?.text ?? ""),
    )
    .join("");
};

const getRowFinancialTone = (row) =>
  DRAWDOWN_LABEL_PATTERN.test(getInlineText(row?.cells?.[0]))
    ? "negative"
    : null;

function renderInlineContent(
  content,
  { colorizeFinancialValues = false, forcedTone = null } = {},
) {
  if (!Array.isArray(content)) return null;
  return content.map((item, i) => {
    if (item.type === "link") {
      return (
        <a
          key={i}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline"
        >
          {renderInlineContent(item.content, { colorizeFinancialValues })}
        </a>
      );
    }
    const s = item.styles ?? {};
    const style = {};
    const adjacentTone = colorizeFinancialValues
      ? getAdjacentFinancialTone(content, i)
      : null;
    if (s.textColor) {
      style.color = resolveTextColor(s.textColor);
      if (isFinancialColor(s.textColor)) style.fontWeight = 500;
    } else if (forcedTone || adjacentTone) {
      style.color = FINANCIAL_COLORS[forcedTone || adjacentTone];
      style.fontWeight = 500;
    }
    if (s.backgroundColor) style.backgroundColor = s.backgroundColor;
    let node =
      colorizeFinancialValues && !s.textColor && !forcedTone && !adjacentTone
        ? renderFinancialText(item.text)
        : item.text;
    if (s.code)
      node = (
        <code className="font-mono text-[12px] bg-[#f0f0f0] px-1 rounded">
          {node}
        </code>
      );
    if (s.bold) node = <strong>{node}</strong>;
    if (s.italic) node = <em>{node}</em>;
    if (s.underline)
      node = <span style={{ textDecoration: "underline" }}>{node}</span>;
    if (s.strikethrough) node = <del>{node}</del>;
    if (Object.keys(style).length)
      return (
        <span key={i} style={style}>
          {node}
        </span>
      );
    return <React.Fragment key={i}>{node}</React.Fragment>;
  });
}

function renderTableCell(cell, options) {
  if (typeof cell === "string") {
    const tone = options?.forcedTone || getFinancialTone(cell);
    if (!options?.colorizeFinancialValues || !tone) return cell;
    return (
      <span style={{ color: FINANCIAL_COLORS[tone], fontWeight: 500 }}>
        {cell}
      </span>
    );
  }
  return renderInlineContent(cell, options);
}

function HeadingBlock({ block, colorizeFinancialValues }) {
  const content = renderInlineContent(block.content, {
    colorizeFinancialValues,
  });
  const level = block.props?.level ?? 2;

  if (level === 3) {
    return <h3 className="text-[14px] font-[700] text-[#111827]">{content}</h3>;
  }

  return <h2 className="text-[14px] font-[400] text-[#111827]">{content}</h2>;
}

function KeyValueTable({ bodyRows, colorizeFinancialValues }) {
  const half = Math.ceil(bodyRows.length / 2);
  const columns = [bodyRows.slice(0, half), bodyRows.slice(half)];

  const renderRow = (row, rowIndex) => (
    <div key={rowIndex} className="grid grid-cols-2 gap-x-2">
      <div className="text-[14px] text-[#636369]">
        {renderTableCell(row.cells[0], { colorizeFinancialValues })}
      </div>
      <div className="text-right text-[14px] font-[500] text-[#111827]">
        {renderTableCell(row.cells[1], {
          colorizeFinancialValues,
          forcedTone: colorizeFinancialValues ? getRowFinancialTone(row) : null,
        })}
      </div>
    </div>
  );

  return (
    <div className="flex">
      {columns.map((rows, columnIndex) => (
        <div
          key={columnIndex}
          className={
            columnIndex === 0
              ? "flex flex-1 flex-col gap-2"
              : "ml-[20px] flex flex-1 flex-col gap-2 border-l border-[#ECECED] pl-[20px]"
          }
        >
          {rows.map(renderRow)}
        </div>
      ))}
    </div>
  );
}

function StandardTable({
  headerRow,
  bodyRows,
  tableStyle,
  colorizeFinancialValues,
}) {
  const isPlain = tableStyle === "plain";
  const containerClass =
    "overflow-hidden rounded-[16px] border border-[#ececed]";
  const headerPadding = isPlain ? "px-[20px] py-[10px]" : "px-[16px] py-[11px]";
  const cellPadding = isPlain ? "px-[20px] py-[10px]" : "px-[16px] py-[8px]";
  const rowBorder = "border-t border-[#ececed]";
  const isTickerColumn = getInlineText(headerRow.cells?.[0]).trim() === "Mã";
  const alignCell = (columnIndex) => {
    if (columnIndex === 0 || !isTickerColumn) return "text-left";
    return "text-right";
  };
  const weightCell = (columnIndex) => {
    if (columnIndex === 0) return isTickerColumn ? "font-[700]" : "font-[400]";
    return "font-[500]";
  };

  return (
    <div
      className={`w-full min-w-0 max-w-full overflow-x-hidden ${containerClass}`}
    >
      <table className="w-full max-w-full table-fixed border-collapse bg-white text-[12px]">
        <thead className="bg-[#f5f5f5]">
          <tr>
            {headerRow.cells.map((cell, columnIndex) => (
              <th
                key={columnIndex}
                className={`${headerPadding} whitespace-normal break-words [overflow-wrap:anywhere] text-[12px] font-[500] leading-[18px] text-[#636369] ${alignCell(columnIndex)}`}
              >
                {renderTableCell(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, rowIndex) => {
            const rowTone = colorizeFinancialValues
              ? getRowFinancialTone(row)
              : null;
            return (
              <tr key={rowIndex} className={rowBorder}>
                {row.cells.map((cell, columnIndex) => (
                  <td
                    key={columnIndex}
                    className={`${cellPadding} ${weightCell(columnIndex)} whitespace-normal break-words [overflow-wrap:anywhere] text-[14px] leading-[20px] text-[#111827] ${alignCell(columnIndex)}`}
                  >
                    {renderTableCell(cell, {
                      colorizeFinancialValues,
                      forcedTone: columnIndex > 0 ? rowTone : null,
                    })}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TableBlock({ block, showTable, tableStyle, colorizeFinancialValues }) {
  const rows = block.content?.rows ?? [];
  if (!rows.length) return null;

  const [headerRow, ...bodyRows] = rows;
  if (showTable && headerRow.cells.length === 2) {
    return (
      <KeyValueTable
        bodyRows={bodyRows}
        colorizeFinancialValues={colorizeFinancialValues}
      />
    );
  }

  return (
    <StandardTable
      headerRow={headerRow}
      bodyRows={bodyRows}
      tableStyle={tableStyle}
      colorizeFinancialValues={colorizeFinancialValues}
    />
  );
}

function ParagraphBlock({ block, colorizeFinancialValues, flushLeft }) {
  const content = renderInlineContent(block.content, {
    colorizeFinancialValues,
  });
  if (!content) return null;
  const isIndustry = getInlineText(block.content).trim().startsWith("Ngành:");

  return (
    <p
      className={`text-[14px] font-[400] leading-[20px] ${
        isIndustry
          ? "mb-[8px] !pl-0 italic text-[#636369]"
          : flushLeft
            ? "!pl-0 text-[#111827]"
            : "text-[#111827]"
      }`}
    >
      {content}
    </p>
  );
}

function BulletList({ blocks, colorizeFinancialValues }) {
  return (
    <ul className="list-disc space-y-[4px] pl-[20px] text-[14px] leading-[20px] text-[#111827]">
      {blocks.map((block) => (
        <li key={block.id}>
          {renderInlineContent(block.content, { colorizeFinancialValues })}
        </li>
      ))}
    </ul>
  );
}

const groupBulletListItems = (blocks) =>
  blocks.reduce((groups, block) => {
    if (block.type !== "bulletListItem") {
      groups.push({ type: "block", block });
      return groups;
    }

    const previousGroup = groups[groups.length - 1];
    if (previousGroup?.type === "bulletList") {
      previousGroup.blocks.push(block);
    } else {
      groups.push({ type: "bulletList", blocks: [block] });
    }
    return groups;
  }, []);

export default function PlanDetailBlockRenderer({
  blocks,
  showTable = false,
  tableStyle = "plain",
  colorizeFinancialValues = false,
  asList = false,
}) {
  if (!Array.isArray(blocks) || !blocks.length) return null;
  if (asList) {
    return (
      <BulletList
        blocks={blocks}
        colorizeFinancialValues={colorizeFinancialValues}
      />
    );
  }
  const blockGroups = groupBulletListItems(blocks);
  return (
    <div className="flex min-w-0 max-w-full flex-col gap-[8px]">
      {blockGroups.map((group, index) => {
        if (group.type === "bulletList") {
          return (
            <BulletList
              key={`bullet-list-${index}`}
              blocks={group.blocks}
              colorizeFinancialValues={colorizeFinancialValues}
            />
          );
        }

        const { block } = group;
        if (block.type === "heading") {
          return (
            <HeadingBlock
              key={block.id}
              block={block}
              colorizeFinancialValues={colorizeFinancialValues}
            />
          );
        }

        if (block.type === "table") {
          return (
            <TableBlock
              key={block.id}
              block={block}
              showTable={showTable}
              tableStyle={tableStyle}
              colorizeFinancialValues={colorizeFinancialValues}
            />
          );
        }

        const previousGroup = blockGroups[index - 1];
        const afterTable =
          previousGroup?.type === "block" &&
          previousGroup.block?.type === "table";

        return (
          <ParagraphBlock
            key={block.id}
            block={block}
            colorizeFinancialValues={colorizeFinancialValues}
            flushLeft={afterTable}
          />
        );
      })}
    </div>
  );
}
